import React, { useRef, useEffect, useState, useCallback } from 'react';
import restaurantService from '../../services/restaurant.service';
import realtimeService from '../../services/realtime.service';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const T = {
  purple: '#552583', gold: '#FDB927',
  text: '#1A1028', sub: '#8A7AA8',
  border: 'rgba(85,37,131,0.08)',
  s1: '#FFFFFF', s2: '#F0EDF6', bg: '#F4F2F8',
  green: '#1A9E64', red: '#D03530',
};

// ── Shared Google Maps loader (mirrors MiniMap.js) ───────────────────────────
const _cbs = [];
function ensureGoogleMaps(cb) {
  if (window.google?.maps) { cb(); return; }
  _cbs.push(cb);
  if (document.querySelector('script[data-gmaps]')) return;
  const s = document.createElement('script');
  s.src = `https://maps.googleapis.com/maps/api/js?v=3.54&key=${GOOGLE_KEY}&libraries=places,geometry&language=fr`;
  s.async = true; s.defer = true;
  s.setAttribute('data-gmaps', '1');
  s.onload = () => { _cbs.forEach(f => f()); _cbs.length = 0; };
  document.head.appendChild(s);
}

// ── Map styles (minimaliste) ─────────────────────────────────────────────────
const MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f5f3ef' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e8e3f2' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d0e8f0' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f8f6fc' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8A7AA8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
];

// ── SVG marker factory ────────────────────────────────────────────────────────
function markerSvg(color, label, size = 32) {
  const svg = `
    <svg width="${size}" height="${size + 8}" viewBox="0 0 ${size} ${size + 8}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="2.5"/>
      <text x="${size / 2}" y="${size / 2 + 5}" text-anchor="middle"
        font-family="Inter,sans-serif" font-size="${size * 0.38}" font-weight="700" fill="white">${label}</text>
      <polygon points="${size / 2 - 5},${size - 2} ${size / 2 + 5},${size - 2} ${size / 2},${size + 7}"
        fill="${color}"/>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function driverSvg(color = T.gold) {
  const svg = `
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="14" r="12" fill="${color}" stroke="white" stroke-width="2.5"/>
      <circle cx="14" cy="14" r="4" fill="white"/>
      <circle cx="14" cy="14" r="2" fill="${color}"/>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// ── Info panel item ───────────────────────────────────────────────────────────
function OrderCard({ order, driverPos, idx }) {
  const statusMap = {
    en_livraison: { label: 'En livraison', color: '#2563EB', bg: 'rgba(59,130,246,0.10)' },
    preparing:    { label: 'En préparation', color: T.purple, bg: 'rgba(85,37,131,0.10)' },
    pending:      { label: 'En attente', color: '#9A6200', bg: 'rgba(253,185,39,0.12)' },
    confirmed:    { label: 'Confirmée', color: T.green, bg: 'rgba(26,158,100,0.10)' },
  };
  const s = statusMap[order.status] || statusMap.pending;
  const name = [order.user?.first_name, order.user?.last_name].filter(Boolean).join(' ') || `Commande #${idx + 1}`;
  const price = Number(order.total_price || order.total || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €';

  return (
    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: T.purple, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
          {idx + 1}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: T.text }}>{name}</div>
          <div style={{ fontSize: '0.65rem', color: T.sub, fontFamily: "'DM Mono', monospace" }}>{price}</div>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, background: s.bg, color: s.color, fontSize: '0.6rem', fontWeight: 600 }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: s.color }} />
          {s.label}
        </div>
      </div>
      {driverPos && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.62rem', color: T.sub }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.gold }} />
          Livreur · {driverPos.lat.toFixed(4)}, {driverPos.lng.toFixed(4)}
        </div>
      )}
      {order.delivery_address?.street && (
        <div style={{ fontSize: '0.62rem', color: T.sub, marginTop: 3 }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2" strokeLinecap="round" style={{ marginRight: 3 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          {order.delivery_address.street}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RestaurantMapView({ restaurant }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({}); // keyed by orderId or 'restaurant'
  const polylinesRef = useRef({});

  const [activeOrders, setActiveOrders] = useState([]);
  const [driverPositions, setDriverPositions] = useState({}); // orderId → {lat, lng}
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const restaurantLat = restaurant?.latitude || restaurant?.lat || -20.8789;
  const restaurantLng = restaurant?.longitude || restaurant?.lng || 55.4481;

  // ── Init map ──────────────────────────────────────────────────────────────
  const initMap = useCallback(() => {
    if (!containerRef.current || mapRef.current) return;
    ensureGoogleMaps(() => {
      if (!containerRef.current) return;
      mapRef.current = new window.google.maps.Map(containerRef.current, {
        center: { lat: restaurantLat, lng: restaurantLng },
        zoom: 14,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'cooperative',
        styles: MAP_STYLES,
      });

      // Restaurant marker
      markersRef.current['restaurant'] = new window.google.maps.Marker({
        position: { lat: restaurantLat, lng: restaurantLng },
        map: mapRef.current,
        title: restaurant?.name || 'Restaurant',
        icon: { url: markerSvg(T.purple, 'R', 36), scaledSize: new window.google.maps.Size(36, 44), anchor: new window.google.maps.Point(18, 44) },
        zIndex: 100,
      });
    });
  }, [restaurantLat, restaurantLng, restaurant]);

  // ── Place/update a marker ─────────────────────────────────────────────────
  const placeMarker = useCallback((key, lat, lng, iconUrl, size, title, zIndex = 10) => {
    if (!mapRef.current || !window.google) return;
    const pos = { lat, lng };
    if (markersRef.current[key]) {
      markersRef.current[key].setPosition(pos);
    } else {
      markersRef.current[key] = new window.google.maps.Marker({
        position: pos, map: mapRef.current, title,
        icon: { url: iconUrl, scaledSize: new window.google.maps.Size(size[0], size[1]), anchor: new window.google.maps.Point(size[0] / 2, size[1]) },
        zIndex,
      });
    }
  }, []);

  const removeMarker = useCallback((key) => {
    if (markersRef.current[key]) {
      markersRef.current[key].setMap(null);
      delete markersRef.current[key];
    }
  }, []);

  const drawRoute = useCallback((key, from, to, color) => {
    if (!mapRef.current || !window.google) return;
    if (polylinesRef.current[key]) {
      polylinesRef.current[key].setMap(null);
    }
    polylinesRef.current[key] = new window.google.maps.Polyline({
      path: [from, to],
      geodesic: true,
      strokeColor: color,
      strokeOpacity: 0.7,
      strokeWeight: 3,
      map: mapRef.current,
    });
  }, []);

  // ── Render orders on map ──────────────────────────────────────────────────
  const renderOrdersOnMap = useCallback((orders, drivers) => {
    if (!mapRef.current || !window.google) return;

    // Remove stale markers
    const activeIds = new Set(orders.map(o => o.id));
    Object.keys(markersRef.current).forEach(key => {
      if (key !== 'restaurant' && !activeIds.has(key.replace('driver_', ''))) {
        removeMarker(key);
      }
    });
    Object.keys(polylinesRef.current).forEach(key => {
      if (!activeIds.has(key.replace('route_', ''))) {
        polylinesRef.current[key]?.setMap(null);
        delete polylinesRef.current[key];
      }
    });

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: restaurantLat, lng: restaurantLng });

    orders.forEach((order, idx) => {
      // Customer delivery address marker
      const addrLat = order.delivery_address?.latitude || order.delivery_address?.lat;
      const addrLng = order.delivery_address?.longitude || order.delivery_address?.lng;
      if (addrLat && addrLng) {
        placeMarker(
          order.id,
          addrLat, addrLng,
          markerSvg(T.gold, String(idx + 1), 28),
          [28, 36],
          `Livraison #${idx + 1}`,
          20 + idx
        );
        bounds.extend({ lat: addrLat, lng: addrLng });

        // Line: restaurant → customer
        drawRoute(`route_${order.id}`, { lat: restaurantLat, lng: restaurantLng }, { lat: addrLat, lng: addrLng }, 'rgba(85,37,131,0.25)');
      }

      // Driver position marker
      const dp = drivers[order.id];
      if (dp) {
        placeMarker(
          `driver_${order.id}`,
          dp.lat, dp.lng,
          driverSvg(T.gold),
          [28, 28],
          `Livreur commande #${idx + 1}`,
          50 + idx
        );
        bounds.extend({ lat: dp.lat, lng: dp.lng });

        // Line: driver → customer (solid)
        if (addrLat && addrLng) {
          drawRoute(`driverroute_${order.id}`, { lat: dp.lat, lng: dp.lng }, { lat: addrLat, lng: addrLng }, '#2563EB');
        }
      }
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    }
  }, [restaurantLat, restaurantLng, placeMarker, removeMarker, drawRoute]);

  // ── Fetch orders ──────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    try {
      const data = await restaurantService.getMyOrders();
      const orders = (Array.isArray(data) ? data : data?.orders || [])
        .filter(o => ['en_livraison', 'preparing', 'confirmed', 'pending'].includes(o.status));
      setActiveOrders(orders);
      setLoading(false);
      return orders;
    } catch {
      setLoading(false);
      return [];
    }
  }, []);

  // ── Subscribe to realtime for all active orders ───────────────────────────
  const subscribeOrders = useCallback((orders) => {
    orders
      .filter(o => o.status === 'en_livraison')
      .forEach(order => {
        realtimeService.joinOrder(order.id);
      });

    realtimeService.onPositionUpdate((payload) => {
      if (!payload?.orderId) return;
      const { orderId, latitude, longitude } = payload;
      setDriverPositions(prev => {
        const next = { ...prev, [orderId]: { lat: latitude, lng: longitude } };
        setLastUpdate(Date.now());
        return next;
      });
    });
  }, []);

  // ── Init sequence ─────────────────────────────────────────────────────────
  useEffect(() => {
    initMap();
    fetchOrders().then(orders => subscribeOrders(orders));

    // Poll orders every 30s
    const poll = setInterval(() => {
      fetchOrders().then(orders => {
        subscribeOrders(orders);
      });
    }, 30000);

    return () => {
      clearInterval(poll);
      realtimeService.removeAllOrderListeners();
      Object.values(markersRef.current).forEach(m => m?.setMap(null));
      Object.values(polylinesRef.current).forEach(p => p?.setMap(null));
      markersRef.current = {};
      polylinesRef.current = {};
    };
  }, [initMap, fetchOrders, subscribeOrders]);

  // ── Re-render when positions or orders change ─────────────────────────────
  useEffect(() => {
    if (mapRef.current && window.google) {
      renderOrdersOnMap(activeOrders, driverPositions);
    }
  }, [activeOrders, driverPositions, renderOrdersOnMap]);

  // ── Pulse lastUpdate to trigger re-render on position changes ─────────────
  useEffect(() => {
    if (lastUpdate && mapRef.current && window.google) {
      renderOrdersOnMap(activeOrders, driverPositions);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdate]);

  const enLivraison = activeOrders.filter(o => o.status === 'en_livraison').length;
  const preparing = activeOrders.filter(o => ['preparing', 'confirmed'].includes(o.status)).length;

  return (
    <div style={{ display: 'flex', height: '100%', gap: 16, overflow: 'hidden' }}>
      {/* Map */}
      <div style={{ flex: 1, borderRadius: 16, overflow: 'hidden', border: `1px solid ${T.border}`, boxShadow: '0 1px 4px rgba(85,37,131,0.06)', position: 'relative', background: T.bg, minHeight: 400 }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

        {/* Overlay legend */}
        <div style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderRadius: 10, padding: '10px 14px', border: `1px solid ${T.border}`, boxShadow: '0 2px 8px rgba(85,37,131,0.08)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: T.text, marginBottom: 8 }}>Légende</div>
          {[
            { color: T.purple, label: 'Restaurant' },
            { color: T.gold, label: 'Livraison' },
            { color: '#2563EB', label: 'Livreur en route' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
              <span style={{ fontSize: '0.65rem', color: T.sub }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Live badge */}
        {enLivraison > 0 && (
          <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(37,99,235,0.12)', borderRadius: 20, padding: '4px 12px', border: '1px solid rgba(37,99,235,0.2)' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2563EB', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#2563EB' }}>Live · {enLivraison} livreur{enLivraison > 1 ? 's' : ''}</span>
          </div>
        )}

        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(244,242,248,0.8)', backdropFilter: 'blur(4px)' }}>
            <div style={{ fontSize: '0.78rem', color: T.sub, fontFamily: "'DM Mono', monospace" }}>Chargement de la carte...</div>
          </div>
        )}
      </div>

      {/* Side panel */}
      <div style={{ width: 260, flexShrink: 0, background: T.s1, borderRadius: 16, border: `1px solid ${T.border}`, boxShadow: '0 1px 4px rgba(85,37,131,0.06)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Panel header */}
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: T.text }}>Commandes actives</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {[
              { n: enLivraison, label: 'livraison', color: '#2563EB', bg: 'rgba(59,130,246,0.10)' },
              { n: preparing, label: 'prép.', color: T.purple, bg: 'rgba(85,37,131,0.10)' },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, background: s.bg, color: s.color, fontSize: '0.62rem', fontWeight: 700 }}>
                {s.n} {s.label}
              </div>
            ))}
          </div>
        </div>

        {/* Order list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: '0.72rem', color: T.sub }}>Chargement...</div>
          ) : activeOrders.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: '0.78rem', color: T.sub }}>Aucune livraison active</div>
              <div style={{ fontSize: '0.65rem', color: T.muted, marginTop: 4 }}>Les nouvelles commandes apparaîtront ici</div>
            </div>
          ) : activeOrders.map((o, i) => (
            <OrderCard key={o.id} order={o} driverPos={driverPositions[o.id]} idx={i} />
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.green }} />
          <span style={{ fontSize: '0.62rem', color: T.sub, fontFamily: "'DM Mono', monospace" }}>
            {lastUpdate ? `Mis à jour ${new Date(lastUpdate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : 'En attente de données live'}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}
